/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @typedef {Object} Category
 * @memberof DataPointSelectionsAPI
 * @property {String} dataItemId dataItem id of the category data
 * @property {String} columnId column id of the category data
 * @property {String} label display label of the category data
 * @property {String} value value of the category data
 */

/**
 * @typedef {Object} Fact
 * @memberof DataPointSelectionsAPI
 * @property {String} dataItemId dataItem id of the fact data
 * @property {String} columnId column id of the fact data
 * @property {String} value value of the fact data
 */

/**
 * @typedef {Object} DataPointSelection
 * @memberof DataPointSelectionsAPI
 * @property {Category[]} categories Array of categeory specs
 * @property {Fact[]} [facts] Array of categeory specs
 */

/**
 * @interface DataPointSelectionsAPI
 * @classdesc API class that is used to control data point selections within a visualization.
 * @example content.getFeature('DataPointSelections')
 */
define([], () => {
	class DataPointSelectionsAPI {

		/**
		 * @function DataPointSelectionsAPI#select
		 * @public
		 * @description Selects data points of the visualization with the given selection spec.
		 * @param {DataPointSelection[]} dataPointSelection Array of data point selection spec
		 * @param {TransactionToken} transactionToken Transaction token
		 * @returns {Promise} resolved when datapoint has been selected
		 * @fires DataPointSelectionsAPI#CanvasAPI change:content:selections:select
		 * @fires DataPointSelectionsAPI#ContentAPI change:selections:select
		 */
		select() {}

		/**
		 * @function DataPointSelectionsAPI#deselect
		 * @public
		 * @description Deselects data points of the visualization with the given selection spec.
		 * @param {DataPointSelection[]} dataPointSelection Array of data point selection spec
		 * @param {TransactionToken} transactionToken Transaction token
		 * @returns {Promise} resolved when datapoint has been deselected
		 * @fires DataPointSelectionsAPI#CanvasAPI change:content:selections:deselect
		 * @fires DataPointSelectionsAPI#ContentAPI change:selections:deselect
		 */
		deselect() {}

		/**
		 * @function DataPointSelectionsAPI#clearAll
		 * @public
		 * @description Clears all data point selections from the visualization
		 * @param {TransactionToken} transactionToken Transaction token
		 * @returns {Promise} resolved when datapoint selection has been cleared
		 * @fires DataPointSelectionsAPI#CanvasAPI change:content:selections:clearAll
		 * @fires DataPointSelectionsAPI#ContentAPI change:selections:clearAll
		 */
		clearAll() {}

		/**
		 * @description Returns the list of data point selections.
		 * @function DataPointSelectionsAPI#getSelections
		 * @public
		 *
		 * @param {Array} selectedDataItemList Optional array containing the selected data items
		 *
		 * @return {DataPointSelection[]} Array of data point selections
		 */
		getSelections(/* selectedDataItemList */) {}

		/**
		 * @function DataPointSelectionsAPI#isSelected
		 * @public
		 * @description Checks whether the given resolved items are selected.
		 *
		 * @param {DataPointSelection[]} dataPointSelection Array of data point selection spec
		 * @return {boolean} True if the resolved items are included in the current selections.
		 */
		isSelected() {}
	}

	return DataPointSelectionsAPI;
});
